import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatNAS, isValidNAS } from "./formatters";
import { Row, ToggleRow, compactInputCls, compactInputMonoCls, sectionTitleCls } from "./RowLayout";

// Format ISO date → AAAA-MM-JJ (pour les champs "Date modification")
const formatDateOnly = (iso) => {
    if (!iso) return "";
    const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : String(iso);
};

export const IdentificationTab = ({
    form, upd, isEditing, readOnly, creating, onTypeChange,
    onCreate,
    taxes = { tps: "", tvq: "", firme: "", found: false, loading: false },
    currentEmail = "",
    onCurrentEmailChange,
    hasCourantAdresse = false,
}) => {
    const isPermanent = (form.type_code === "A")
        || (form.code || "").toUpperCase().startsWith("A");

    const nasDigits = String(form.nas || "").replace(/\D/g, "");
    const nasInvalid = nasDigits.length > 0 && !isValidNAS(nasDigits);

    // Helpers toggle : verrouille tous les flags sauf « actif », « mega »
    // et « payable » pour les permanents.
    const toggleLocked = (k) => isPermanent && k !== "actif" && k !== "mega" && k !== "payable";
    const toggleChecked = (k) => toggleLocked(k) ? false : !!form[k];

    const renderToggle = (k, label) => (
        <ToggleRow
            key={k}
            k={k}
            label={label}
            locked={toggleLocked(k)}
            checked={toggleChecked(k)}
            onChange={(v) => upd(k, v)}
            disabled={readOnly}
        />
    );

    return (
        <form
            onSubmit={(e) => { e.preventDefault(); if (!isEditing) onCreate?.(); }}
            className="space-y-3"
            data-testid="avocat-form"
        >
            {isPermanent && (
                <p className="text-xs text-slate-500 italic" data-testid="permanent-hint">
                    Avocat permanent (type A) — seuls les statuts « Actif », « Méga » et « Payable » sont applicables.
                </p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
                {/* ============= COLONNE GAUCHE — champs texte ============= */}
                <div>
                    <Row label="Type de code">
                        <Select value={form.type_code} onValueChange={onTypeChange} disabled={isEditing || readOnly}>
                            <SelectTrigger className={compactInputCls} data-testid="avocat-type-code">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="A">A — Avocat permanent</SelectItem>
                                <SelectItem value="P">P — Avocat privé</SelectItem>
                                <SelectItem value="N">N — Notaire</SelectItem>
                            </SelectContent>
                        </Select>
                    </Row>
                    <Row label={isEditing ? "Code" : "Code (généré)"}>
                        <Input
                            value={isEditing ? (form.code || "") : ""}
                            placeholder={isEditing ? "" : "— auto —"}
                            disabled
                            className={`${compactInputMonoCls} bg-slate-50`}
                            data-testid="avocat-input-code"
                        />
                    </Row>
                    <Row label="Nom" required>
                        <Input value={form.nom} onChange={(e) => upd("nom", e.target.value)} disabled={readOnly} required className={compactInputCls} data-testid="avocat-input-nom" />
                    </Row>
                    <Row label="Prénom" required>
                        <Input value={form.prenom} onChange={(e) => upd("prenom", e.target.value)} disabled={readOnly} required className={compactInputCls} data-testid="avocat-input-prenom" />
                    </Row>
                    <Row label="Année barreau">
                        <Input
                            value={form.annee_barreau || ""}
                            onChange={(e) => upd("annee_barreau", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            disabled={readOnly}
                            maxLength={4}
                            inputMode="numeric"
                            pattern="[0-9]{4}"
                            placeholder="AAAA"
                            className={compactInputMonoCls}
                            data-testid="avocat-input-annee-barreau"
                        />
                    </Row>
                    <Row label="Code barreau">
                        <Input value={form.codebar} onChange={(e) => upd("codebar", e.target.value)} disabled={readOnly} className={compactInputMonoCls} />
                    </Row>
                    <Row label="NAS">
                        <Input
                            value={formatNAS(form.nas)}
                            onChange={(e) => upd("nas", e.target.value.replace(/\D/g, "").slice(0, 9))}
                            disabled={readOnly}
                            maxLength={11}
                            placeholder="### ### ###"
                            className={`${compactInputMonoCls} ${nasInvalid ? "border-red-500" : ""}`}
                            data-testid="avocat-input-nas"
                        />
                        {nasInvalid && (
                            <p className="text-[10px] text-red-600 mt-0.5" data-testid="err-nas">
                                {nasDigits.length === 9
                                    ? "NAS invalide (Luhn)"
                                    : "Le NAS doit comporter 9 chiffres"}
                            </p>
                        )}
                    </Row>
                    <Row label="Ville référence">
                        <Input
                            value={form.villerref || ""}
                            disabled
                            className={`${compactInputCls} bg-slate-50`}
                            data-testid="avocat-input-villerref"
                        />
                    </Row>
                    <Row label={`Firme${taxes.loading ? " (…)" : ""}`}>
                        <Input
                            value={taxes.firme || ""}
                            disabled
                            placeholder={taxes.loading ? "…" : (taxes.found ? "—" : "non renseignée (Fvi)")}
                            className={`${compactInputCls} bg-slate-50 text-slate-600`}
                            data-testid="avocat-input-firme"
                        />
                    </Row>
                </div>

                {/* ============= COLONNE DROITE — toggles + métadonnées ============= */}
                <div>
                    {renderToggle("actif", "Actif")}
                    {renderToggle("factweb", "Facturation web")}
                    {renderToggle("payable", "Payable")}
                    {renderToggle("surveil", "En attente")}
                    {renderToggle("depodirect", "Dépôt direct")}
                    {renderToggle("mega", "Méga")}
                    {renderToggle("confweb", "Confirmation par courriel")}
                    <Row label={`TPS — n° taxe${taxes.loading ? " (…)" : ""}`}>
                        <div className="flex justify-end">
                            <Input
                                value={taxes.tps || ""}
                                disabled
                                placeholder={taxes.loading ? "…" : (taxes.found ? "—" : "non renseigné (Fvi)")}
                                className={`${compactInputMonoCls} text-slate-600 bg-slate-50 w-[260px] text-right`}
                                data-testid="avocat-input-tps"
                            />
                        </div>
                    </Row>
                    <Row label={`TVQ — n° taxe${taxes.loading ? " (…)" : ""}`}>
                        <div className="flex justify-end">
                            <Input
                                value={taxes.tvq || ""}
                                disabled
                                placeholder={taxes.loading ? "…" : (taxes.found ? "—" : "non renseigné (Fvi)")}
                                className={`${compactInputMonoCls} text-slate-600 bg-slate-50 w-[260px] text-right`}
                                data-testid="avocat-input-tvq"
                            />
                        </div>
                    </Row>
                    <Row label="Date modification">
                        <div className="flex justify-end">
                            <Input
                                value={formatDateOnly(form.updated_at)}
                                disabled
                                className={`${compactInputMonoCls} text-slate-600 bg-slate-50 w-[260px] text-right`}
                                data-testid="avocat-updated-at"
                            />
                        </div>
                    </Row>
                    <Row label="Modifié par">
                        <div className="flex justify-end">
                            <Input
                                value={form.usermodif || ""}
                                disabled
                                className={`${compactInputCls} text-slate-600 bg-slate-50 w-[260px] text-right`}
                                data-testid="avocat-usermodif"
                            />
                        </div>
                    </Row>
                </div>
            </div>

            {/* ============= EN BAS — Adresse courriel (info) + Commentaires ============= */}
            <div className="pt-1 border-t border-slate-200" />
            <Row label="Adresse courriel">
                <Input
                    type="email"
                    value={currentEmail}
                    onChange={(e) => onCurrentEmailChange?.(e.target.value)}
                    disabled={readOnly || !isEditing || !hasCourantAdresse}
                    placeholder={
                        !isEditing
                            ? "— créez d'abord l'avocat —"
                            : !hasCourantAdresse
                                ? "— ajoutez une adresse courante dans l'onglet Adresses —"
                                : "exemple@cabinet.qc.ca"
                    }
                    className={compactInputCls}
                    data-testid="avocat-current-email"
                />
            </Row>
            <div>
                <Label className={sectionTitleCls}>Commentaires</Label>
                <Textarea
                    value={form.comm}
                    onChange={(e) => upd("comm", e.target.value)}
                    disabled={readOnly}
                    rows={3}
                    className="rounded-md mt-1 text-sm"
                    data-testid="avocat-input-comm"
                />
            </div>

            {!isEditing && !readOnly && (
                <div className="flex justify-end pt-1">
                    <Button type="submit" disabled={creating} className="rounded-md bg-[#0033A0] hover:bg-[#002277] text-white" data-testid="avocat-submit">
                        {creating ? "Création…" : "Créer"}
                    </Button>
                </div>
            )}
            {isEditing && !readOnly && (
                <p className="text-[11px] text-slate-500 italic">
                    Cliquez sur « Sauvegarder » en haut à droite pour enregistrer vos modifications.
                </p>
            )}
        </form>
    );
};
